import React, { useState, useEffect } from 'react';
import './ManageCourse.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaCopy,
  FaClock,
  FaBook,
  FaSearch,
  FaTimes,
  FaSave,
  FaChevronDown,
  FaChevronUp,
  FaClipboardCheck,
  FaFolder,
  FaClipboardList,
  FaDownload,
  FaUpload,
  FaFileImport,
} from 'react-icons/fa';

const ManageCourse = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedChecklists, setExpandedChecklists] = useState({});
  const [expandedAssessments, setExpandedAssessments] = useState({});
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [showChecklistForm, setShowChecklistForm] = useState(false);
  const [showAssessmentForm, setShowAssessmentForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingModuleIndex, setEditingModuleIndex] = useState(null);
  const [showCopyChecklistPanel, setShowCopyChecklistPanel] = useState(false);
  const [showCopyAssessmentPanel, setShowCopyAssessmentPanel] = useState(false);
  const [copySourceCourseId, setCopySourceCourseId] = useState('');
  const [copySelectedItems, setCopySelectedItems] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importCodeOverride, setImportCodeOverride] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importCodeConflict, setImportCodeConflict] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    duration: '',
    description: '',
    level: 'PG',
    modules: [],
    checklists: [],
    assessments: []
  });
  
  const [moduleData, setModuleData] = useState({
    name: '',
    type: 'entries',
    fields: [],
    mainHeading: '',
    rowElements: [],
    fieldGroups: [],
    allowAttachments: false
  });
  
  const [rowElementData, setRowElementData] = useState({
    label: '',
    inputType: 'number',
    options: []
  });
  
  const [fieldGroupData, setFieldGroupData] = useState({
    groupName: '',
    fields: []
  });
  
  const [checklistData, setChecklistData] = useState({
    name: '',
    observationItems: [],
    maxScore: 4
  });
  
  const [assessmentData, setAssessmentData] = useState({
    name: '',
    type: 'overall',
    gradeScale: 'A-J',
    customGrades: '',
    selectedMonths: [],
    assessmentItems: []
  });
  
  const [assessmentItemInput, setAssessmentItemInput] = useState('');
  
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    label: `Month ${i + 1}`
  }));
  
  const monthPresets = [
    { name: 'Quarterly', months: [3, 6, 9, 12] },
    { name: 'Bi-Monthly', months: [2, 4, 6, 8, 10, 12] },
    { name: 'Semi-Annual', months: [6, 12] },
    { name: 'All Months', months: Array.from({ length: 12 }, (_, i) => i + 1) }
  ];
  
  const [observationItemInput, setObservationItemInput] = useState('');
  
  const [fieldData, setFieldData] = useState({
    label: '',
    type: 'text',
    required: false,
    options: [],
    validation: {}
  });

  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [currentGroupFields, setCurrentGroupFields] = useState([]);

  useEffect(() => {
    fetchCourses();
  }, []);

  // Reconstruct moduleData structure from a saved module (handles both fresh + server-loaded)
  const reconstructModuleForEdit = (module) => {
    if (module.type === 'counters') {
      if (module.rowElements && module.rowElements.length > 0) {
        return {
          name: module.name,
          type: module.type,
          mainHeading: module.mainHeading || '',
          rowElements: module.rowElements,
          fields: [],
          fieldGroups: [],
          isActive: module.isActive !== false
        };
      }
      const rowElements = (module.fields || []).map((field, i) => ({
        label: field.label,
        inputType: field.type === 'select' ? 'select' : 'number',
        options: field.options || [],
        id: `${Date.now()}_${i}`
      }));
      return {
        name: module.name,
        type: module.type,
        mainHeading: module.mainHeading || '',
        rowElements,
        fields: [],
        fieldGroups: [],
        isActive: module.isActive !== false,
        allowAttachments: module.allowAttachments || false
      };
    } else {
      if (module.fieldGroups && module.fieldGroups.length > 0) {
        return {
          name: module.name,
          type: module.type,
          fields: module.fields || [],
          fieldGroups: module.fieldGroups,
          rowElements: [],
          mainHeading: '',
          isActive: module.isActive !== false
        };
      }
      const flatFields = module.fields || [];
      const ungroupedFields = flatFields.filter(f => !f.groupName);
      const groupedFields = flatFields.filter(f => f.groupName);
      const groupMap = {};
      const groupOrder = [];
      groupedFields.forEach(f => {
        if (!groupMap[f.groupName]) {
          groupMap[f.groupName] = [];
          groupOrder.push(f.groupName);
        }
        groupMap[f.groupName].push(f);
      });
      const fieldGroups = groupOrder.map((groupName, i) => ({
        groupName,
        fields: groupMap[groupName],
        id: `${Date.now()}_${i}`
      }));
      return {
        name: module.name,
        type: module.type,
        fields: ungroupedFields,
        fieldGroups,
        rowElements: [],
        mainHeading: '',
        isActive: module.isActive !== false,
        allowAttachments: module.allowAttachments || false
      };
    }
  };

  // Get assessment groups for display in copy panel
  const getAssessmentGroups = (assessments) => {
    const groups = [];
    const seenParents = new Set();
    (assessments || []).forEach(a => {
      if (a.isYearlyPart && a.parentAssessmentName) {
        if (!seenParents.has(a.parentAssessmentName)) {
          seenParents.add(a.parentAssessmentName);
          groups.push({
            type: 'yearly_group',
            name: a.parentAssessmentName,
            assessments: assessments.filter(x => x.parentAssessmentName === a.parentAssessmentName)
          });
        }
      } else {
        groups.push({ type: 'overall', name: a.name, id: a.assessmentId, assessment: a });
      }
    });
    return groups;
  };

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
      } else {
        setError('Failed to fetch courses');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const url = modalMode === 'create' 
        ? `${collegeConfig.api.baseURL}/courses`
        : `${collegeConfig.api.baseURL}/courses/${selectedCourse._id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      
      const transformedModules = formData.modules.map(module => {
        if (module.type === 'counters') {
          const rowElements = module.rowElements || [];
          const fields = rowElements.map((element, index) => ({
            key: element.label.toLowerCase().replace(/\s+/g, '_'),
            label: element.label,
            type: element.inputType === 'select' ? 'select' : 'counter',
            options: element.options || [],
            required: false
          }));
          
          return {
            ...module,
            fields,
            mainHeading: module.mainHeading
          };
        } else {
          let fields = [...(module.fields || [])];
          
          if (module.fieldGroups && Array.isArray(module.fieldGroups)) {
            module.fieldGroups.forEach(group => {
              if (group.fields && Array.isArray(group.fields)) {
                group.fields.forEach(field => {
                  fields.push({
                    ...field,
                    key: field.key || field.label.toLowerCase().replace(/\s+/g, '_'),
                    groupName: group.groupName
                  });
                });
              }
            });
          }
          
          return {
            ...module,
            fields
          };
        }
      });
      
      const requestData = {
        ...formData,
        modules: transformedModules
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(data.message);
        fetchCourses();
        closeModal();
      } else {
        setError(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving course:', error);
      setError('Error connecting to server: ' + error.message);
    }
  };

  const handleExportCourse = async (courseId, courseName, courseCode) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/courses/${courseId}/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!data.success) {
        setError(data.message || 'Failed to export course');
        return;
      }
      const blob = new Blob([JSON.stringify(data.exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${courseCode}_course_export.json`;
      a.click();
      URL.revokeObjectURL(url);
      setSuccess(`Course "${courseName}" exported successfully`);
    } catch (err) {
      setError('Error exporting course: ' + err.message);
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        if (parsed.systemType !== 'pmch-elogbook' || !parsed.course) {
          setImportError('Invalid file: not a PMCH E-Logbook course export');
          setImportPreview(null);
          return;
        }
        setImportPreview(parsed);
        setImportCodeOverride(parsed.course.code);
        setImportError('');
        setImportCodeConflict(false);
      } catch {
        setImportError('Invalid JSON file');
        setImportPreview(null);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImportSubmit = async () => {
    if (!importPreview) return;
    setImportLoading(true);
    setImportError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/courses/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          exportData: importPreview,
          overrideCode: importCodeOverride
        })
      });
      const data = await response.json();
      if (!data.success) {
        setImportError(data.message || 'Import failed');
        if (data.codeConflict) setImportCodeConflict(true);
        return;
      }
      setSuccess(data.message);
      setShowImportModal(false);
      setImportPreview(null);
      setImportCodeOverride('');
      setImportCodeConflict(false);
      fetchCourses();
    } catch (err) {
      setImportError('Error connecting to server: ' + err.message);
    } finally {
      setImportLoading(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportPreview(null);
    setImportCodeOverride('');
    setImportError('');
    setImportCodeConflict(false);
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/courses/${courseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setSuccess('Course deleted successfully');
        fetchCourses();
      } else {
        setError('Failed to delete course');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      setError('Error connecting to server');
    }
  };

  const openModal = (mode, course = null) => {
    setModalMode(mode);
    setSelectedCourse(course);
    setShowModuleForm(false);
    setShowChecklistForm(false);
    setShowAssessmentForm(false);
    setError('');
    
    if (mode === 'create') {
      setFormData({
        name: '',
        code: '',
        duration: '',
        description: '',
        level: 'PG',
        modules: [],
        checklists: [],
        assessments: []
      });
    } else if (course) {
      setFormData({
        name: course.name,
        code: course.code,
        duration: course.duration,
        description: course.description || '',
        level: course.level || 'PG',
        modules: course.modules || [],
        checklists: course.checklists || [],
        assessments: course.assessments || []
      });
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCourse(null);
    setFormData({
      name: '',
      code: '',
      duration: '',
      description: '',
      level: 'PG',
      modules: [],
      checklists: [],
      assessments: []
    });
    resetModuleForm();
    resetAssessmentForm();
    setChecklistData({
      name: '',
      observationItems: [],
      maxScore: 4
    });
  };

  const resetModuleForm = () => {
    setModuleData({
      name: '',
      type: 'entries',
      fields: [],
      mainHeading: '',
      rowElements: [],
      fieldGroups: [],
      allowAttachments: false
    });
    setRowElementData({
      label: '',
      inputType: 'number',
      options: []
    });
    setFieldGroupData({
      groupName: '',
      fields: []
    });
    setFieldData({
      label: '',
      type: 'text',
      required: false,
      options: [],
      validation: {}
    });
    setIsCreatingGroup(false);
    setCurrentGroupFields([]);
  };

  const resetAssessmentForm = () => {
    setAssessmentData({
      name: '',
      type: 'overall',
      gradeScale: 'A-J',
      customGrades: '',
      selectedMonths: [],
      assessmentItems: []
    });
    setAssessmentItemInput('');
  };

  const addAssessment = () => {
    if (!assessmentData.name) {
      setError('Assessment name is required');
      return;
    }
    
    if (assessmentData.assessmentItems.length === 0) {
      setError('At least one assessment item is required');
      return;
    }
    
    if (assessmentData.type === 'yearly' && assessmentData.selectedMonths.length === 0) {
      setError('Please select at least one month for yearly assessment');
      return;
    }
    
    let assessmentsToAdd = [];
    
    if (assessmentData.type === 'yearly') {
      // Create 4 assessments: Year 1, Year 2, Year 3, and Overall
      const baseItems = assessmentData.assessmentItems.map(item => ({
        id: item.id,
        name: item.name,
        order: item.order
      }));
      
      // Year 1, 2, 3 assessments with selected months
      for (let year = 1; year <= 3; year++) {
        assessmentsToAdd.push({
          assessmentId: `${Date.now()}_year${year}`,
          name: `${assessmentData.name} - Year ${year}`,
          type: 'yearly',
          yearNumber: year,
          periodType: 'monthly',
          selectedMonths: assessmentData.selectedMonths,
          assessmentItems: baseItems,
          isYearlyPart: true,
          parentAssessmentName: assessmentData.name
        });
      }
      
      // Overall assessment with Year 1, 2, 3 columns
      assessmentsToAdd.push({
        assessmentId: `${Date.now()}_overall`,
        name: `${assessmentData.name} - Overall`,
        type: 'yearly',
        yearNumber: 'overall',
        periodType: 'yearly',
        selectedYears: [
          { year: 1, label: 'Year 1' },
          { year: 2, label: 'Year 2' },
          { year: 3, label: 'Year 3' },
          { year: 'overall', label: 'Overall' }
        ],
        assessmentItems: baseItems,
        isYearlyPart: true,
        isOverallSheet: true,
        parentAssessmentName: assessmentData.name
      });
    } else {
      // Overall type - single assessment with A-J grades
      assessmentsToAdd.push({
        assessmentId: Date.now().toString(),
        name: assessmentData.name,
        type: 'overall',
        gradeScale: assessmentData.gradeScale,
        customGrades: assessmentData.gradeScale === 'custom' && assessmentData.customGrades 
          ? assessmentData.customGrades.split(',').map(g => g.trim()) 
          : [],
        assessmentItems: assessmentData.assessmentItems.map(item => ({
          id: item.id,
          name: item.name,
          order: item.order
        }))
      });
    }
    
    setFormData({
      ...formData,
      assessments: [...formData.assessments, ...assessmentsToAdd]
    });
    
    resetAssessmentForm();
    setShowAssessmentForm(false);
    setError('');
  };

  const removeAssessment = (index) => {
    const assessment = formData.assessments[index];
    
    // If it's a yearly assessment, remove all related assessments
    if (assessment.isYearlyPart) {
      const parentName = assessment.parentAssessmentName;
      const updatedAssessments = formData.assessments.filter(
        a => a.parentAssessmentName !== parentName
      );
      setFormData({ ...formData, assessments: updatedAssessments });
    } else {
      const updatedAssessments = formData.assessments.filter((_, i) => i !== index);
      setFormData({ ...formData, assessments: updatedAssessments });
    }
  };

  const addAssessmentItem = () => {
    if (!assessmentItemInput.trim()) {
      setError('Assessment item cannot be empty');
      return;
    }
    
    const newItem = {
      id: Date.now().toString(),
      name: assessmentItemInput.trim(),
      order: assessmentData.assessmentItems.length
    };
    
    setAssessmentData({
      ...assessmentData,
      assessmentItems: [...assessmentData.assessmentItems, newItem]
    });
    setAssessmentItemInput('');
    setError('');
  };

  const removeAssessmentItem = (index) => {
    const updatedItems = assessmentData.assessmentItems.filter((_, i) => i !== index);
    setAssessmentData({ ...assessmentData, assessmentItems: updatedItems });
  };

  const toggleMonthSelection = (month) => {
    const exists = assessmentData.selectedMonths.find(m => m.month === month);
    
    if (exists) {
      setAssessmentData({
        ...assessmentData,
        selectedMonths: assessmentData.selectedMonths.filter(m => m.month !== month)
      });
    } else {
      setAssessmentData({
        ...assessmentData,
        selectedMonths: [...assessmentData.selectedMonths, { 
          month, 
          label: `Month ${month}` 
        }].sort((a, b) => a.month - b.month)
      });
    }
  };

  const applyMonthPreset = (preset) => {
    const selectedMonths = preset.months.map(month => ({
      month,
      label: `Month ${month}`
    }));
    setAssessmentData({ ...assessmentData, selectedMonths });
  };

  const saveModule = () => {
    if (!moduleData.name) {
      setError('Module name is required');
      return;
    }

    if (moduleData.type === 'counters' && !moduleData.mainHeading) {
      setError('Main heading is required for row-wise modules');
      return;
    }

    if (moduleData.type === 'counters' && moduleData.rowElements.length === 0) {
      setError('At least one row element is required for row-wise modules');
      return;
    }

    if (editingModuleIndex !== null) {
      const updatedModules = [...formData.modules];
      updatedModules[editingModuleIndex] = {
        ...moduleData,
        moduleId: formData.modules[editingModuleIndex].moduleId || Date.now().toString(),
        order: formData.modules[editingModuleIndex].order ?? editingModuleIndex,
        isActive: moduleData.isActive !== false
      };
      setFormData({ ...formData, modules: updatedModules });
      setEditingModuleIndex(null);
    } else {
      const newModule = {
        ...moduleData,
        moduleId: Date.now().toString(),
        order: formData.modules.length,
        isActive: true
      };
      setFormData({ ...formData, modules: [...formData.modules, newModule] });
    }

    resetModuleForm();
    setShowModuleForm(false);
    setError('');
  };

  const removeModule = (index) => {
    const updatedModules = formData.modules.filter((_, i) => i !== index);
    setFormData({ ...formData, modules: updatedModules });
    if (editingModuleIndex === index) {
      setEditingModuleIndex(null);
      resetModuleForm();
      setShowModuleForm(false);
    }
  };

  const startEditModule = (index) => {
    const module = formData.modules[index];
    const reconstructed = reconstructModuleForEdit(module);
    setModuleData(reconstructed);
    setEditingModuleIndex(index);
    setShowModuleForm(true);
  };

  const duplicateModule = (index) => {
    const original = formData.modules[index];
    const copy = {
      ...JSON.parse(JSON.stringify(original)), // deep clone
      moduleId: Date.now().toString(),
      name: `${original.name} (Copy)`,
      order: formData.modules.length,
      isActive: true
    };
    setFormData({ ...formData, modules: [...formData.modules, copy] });
  };

  const toggleModuleVisibility = (index) => {
    const updatedModules = [...formData.modules];
    updatedModules[index] = {
      ...updatedModules[index],
      isActive: updatedModules[index].isActive === false ? true : false
    };
    setFormData({ ...formData, modules: updatedModules });
  };

  const toggleCopyItem = (itemName) => {
    setCopySelectedItems(prev =>
      prev.includes(itemName) ? prev.filter(i => i !== itemName) : [...prev, itemName]
    );
  };

  const copyChecklistsFromCourse = () => {
    const source = courses.find(c => c._id === copySourceCourseId);
    if (!source) return;
    const selected = (source.checklists || []).filter(cl => copySelectedItems.includes(cl.checklistId));
    const copied = selected.map(cl => ({
      ...cl,
      checklistId: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 6)
    }));
    setFormData({ ...formData, checklists: [...formData.checklists, ...copied] });
    setCopySelectedItems([]);
    setCopySourceCourseId('');
    setShowCopyChecklistPanel(false);
  };

  const copyAssessmentsFromCourse = () => {
    const source = courses.find(c => c._id === copySourceCourseId);
    if (!source) return;
    const groups = getAssessmentGroups(source.assessments || []);
    const selectedGroups = groups.filter(g => copySelectedItems.includes(g.name));
    let newAssessments = [];
    selectedGroups.forEach(group => {
      if (group.type === 'yearly_group') {
        group.assessments.forEach(a => {
          newAssessments.push({
            ...a,
            assessmentId: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 6)
          });
        });
      } else {
        newAssessments.push({
          ...group.assessment,
          assessmentId: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 6)
        });
      }
    });
    setFormData({ ...formData, assessments: [...formData.assessments, ...newAssessments] });
    setCopySelectedItems([]);
    setCopySourceCourseId('');
    setShowCopyAssessmentPanel(false);
  };

  const addRowElement = () => {
    if (!rowElementData.label) {
      setError('Row element label is required');
      return;
    }
    
    if (rowElementData.inputType === 'select' && (!rowElementData.options || rowElementData.options.length === 0)) {
      setError('Options are required for select type');
      return;
    }
    
    const newRowElement = {
      ...rowElementData,
      id: Date.now().toString()
    };
    
    setModuleData({
      ...moduleData,
      rowElements: [...moduleData.rowElements, newRowElement]
    });
    
    setRowElementData({
      label: '',
      inputType: 'number',
      options: []
    });
    setError('');
  };

  const removeRowElement = (index) => {
    const updatedElements = moduleData.rowElements.filter((_, i) => i !== index);
    setModuleData({ ...moduleData, rowElements: updatedElements });
  };

  const addFieldToCurrentGroup = () => {
    if (!fieldData.label) {
      setError('Field label is required');
      return;
    }
    
    const newField = {
      ...fieldData,
      key: fieldData.label.toLowerCase().replace(/\s+/g, '_')
    };
    
    if (fieldData.type === 'select' && typeof fieldData.options === 'string') {
      newField.options = fieldData.options.split(',').map(opt => opt.trim());
    }
    
    setCurrentGroupFields([...currentGroupFields, newField]);
    
    setFieldData({
      label: '',
      type: 'text',
      required: false,
      options: [],
      validation: {}
    });
    setError('');
  };

  const removeFieldFromCurrentGroup = (index) => {
    const updatedFields = currentGroupFields.filter((_, i) => i !== index);
    setCurrentGroupFields(updatedFields);
  };

  const saveFieldGroup = () => {
    if (!fieldGroupData.groupName) {
      setError('Group name is required');
      return;
    }
    
    if (currentGroupFields.length === 0) {
      setError('At least one field is required in the group');
      return;
    }
    
    const newGroup = {
      groupName: fieldGroupData.groupName,
      fields: currentGroupFields,
      id: Date.now().toString()
    };
    
    setModuleData({
      ...moduleData,
      fieldGroups: [...moduleData.fieldGroups, newGroup]
    });
    
    setFieldGroupData({
      groupName: '',
      fields: []
    });
    setCurrentGroupFields([]);
    setIsCreatingGroup(false);
    setError('');
  };

  const removeFieldGroup = (index) => {
    const updatedGroups = moduleData.fieldGroups.filter((_, i) => i !== index);
    setModuleData({ ...moduleData, fieldGroups: updatedGroups });
  };

  const addFieldToModule = () => {
    if (!fieldData.label) {
      setError('Field label is required');
      return;
    }
    
    const newField = {
      ...fieldData,
      key: fieldData.label.toLowerCase().replace(/\s+/g, '_')
    };
    
    if (newField.type === 'select' && typeof newField.options === 'string') {
      newField.options = newField.options.split(',').map(opt => opt.trim());
    }
    
    setModuleData({
      ...moduleData,
      fields: [...moduleData.fields, newField]
    });
    
    setFieldData({
      label: '',
      type: 'text',
      required: false,
      options: [],
      validation: {}
    });
    setError('');
  };

  const removeFieldFromModule = (index) => {
    const updatedFields = moduleData.fields.filter((_, i) => i !== index);
    setModuleData({ ...moduleData, fields: updatedFields });
  };

  const addChecklist = () => {
    if (!checklistData.name) {
      setError('Checklist name is required');
      return;
    }
    
    if (checklistData.observationItems.length === 0) {
      setError('At least one observation item is required');
      return;
    }
    
    const newChecklist = {
      ...checklistData,
      checklistId: Date.now().toString(),
    };
    
    setFormData({
      ...formData,
      checklists: [...formData.checklists, newChecklist]
    });
    
    setChecklistData({
      name: '',
      observationItems: [],
      maxScore: 4
    });
    setShowChecklistForm(false);
    setError('');
  };

  const removeChecklist = (index) => {
    const updatedChecklists = formData.checklists.filter((_, i) => i !== index);
    setFormData({ ...formData, checklists: updatedChecklists });
  };

  const addObservationItem = () => {
    if (!observationItemInput.trim()) {
      setError('Observation item cannot be empty');
      return;
    }
    
    setChecklistData({
      ...checklistData,
      observationItems: [...checklistData.observationItems, {
        id: Date.now().toString(),
        name: observationItemInput.trim()
      }]
    });
    setObservationItemInput('');
    setError('');
  };

  const removeObservationItem = (index) => {
    const updatedItems = checklistData.observationItems.filter((_, i) => i !== index);
    setChecklistData({ ...checklistData, observationItems: updatedItems });
  };

  const toggleModuleExpansion = (courseId) => {
    setExpandedModules(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };

  const toggleChecklistExpansion = (courseId) => {
    setExpandedChecklists(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };

  const toggleAssessmentExpansion = (courseId) => {
    setExpandedAssessments(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };

  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <div className="manage-course-loading">
        <div className="spinner"></div>
        <p>Loading courses...</p>
      </div>
    );
  }

  return (
    <div className="manage-course-container">
      <div className="manage-course-header">
        <div className="header-content">
          <h2>Manage Courses</h2>
          <p>Create and manage medical courses and their modules</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-import-course"
            onClick={() => setShowImportModal(true)}
          >
            <FaFileImport /> Import Course
          </button>
          <button
            className="btn-create-course"
            onClick={() => openModal('create')}
          >
            <FaPlus /> Create New Course
          </button>
        </div>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="search-section">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search courses by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="courses-grid">
        {filteredCourses.length === 0 ? (
          <div className="no-courses">
            <FaBook className="no-courses-icon" />
            <h3>No courses found</h3>
            <p>Create your first course to get started</p>
          </div>
        ) : (
          filteredCourses.map(course => (
            <div key={course._id} className="course-card">
              <div className="course-card-header">
                <div className="course-info">
                  <h3>{course.name}</h3>
                  <div className="course-code-level">
                    <span className="course-code1">{course.code}</span>
                    <span className={`course-level-badge course-level-${(course.level || 'PG').replace(' ', '-')}`}>
                      {course.level || 'PG'}
                    </span>
                  </div>
                </div>
                <div className="course-actions">
                  <button 
                    className="btn-icon btn-view"
                    onClick={() => openModal('view', course)}
                    title="View Details"
                  >
                    <FaEye />
                  </button>
                  <button
                    className="btn-icon btn-edit"
                    onClick={() => openModal('edit', course)}
                    title="Edit Course"
                  >
                    <FaEdit />
                  </button>
                  <button
                    className="btn-icon btn-export"
                    onClick={() => handleExportCourse(course._id, course.name, course.code)}
                    title="Export Course"
                  >
                    <FaDownload />
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(course._id)}
                    title="Delete Course"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
              
              <div className="course-card-body">
                <p className="course-description">
                  {course.description || 'No description available'}
                </p>
                
                <div className="course-stats1">
                  <div className="stat2">
                    <FaClock className="stat-icon4" />
                    <span>{course.duration} months</span>
                  </div>
                  <div className="stat2">
                    <FaBook className="stat-icon4" />
                    <span>
                      {course.modules?.filter(m => m.isActive !== false).length || 0}
                      {course.modules?.some(m => m.isActive === false) ? `/${course.modules.length}` : ''} modules
                    </span>
                  </div>
                  <div className="stat2">
                    <FaClipboardCheck className="stat-icon4" />
                    <span>{course.checklists?.length || 0} checklists</span>
                  </div>
                  <div className="stat2">
                    <FaClipboardList className="stat-icon4" />
                    <span>{course.assessments?.length || 0} assessments</span>
                  </div>
                </div>
                
                {course.modules && course.modules.length > 0 && (
                  <div className="course-modules-preview">
                    <button 
                      className="modules-toggle"
                      onClick={() => toggleModuleExpansion(course._id)}
                    >
                      Modules {expandedModules[course._id] ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    
                    {expandedModules[course._id] && (
                      <div className="modules-list">
                        {course.modules.map((module, index) => (
                          <div key={module.moduleId} className={`module-item ${module.isActive === false ? 'module-item-hidden' : ''}`}>
                            <span className="module-number">{index + 1}</span>
                            <span className="module-name">{module.name}</span>
                            <span className="module-type">{module.type}</span>
                            {module.isActive === false && <span className="module-hidden-badge">Hidden</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {course.checklists && course.checklists.length > 0 && (
                  <div className="course-checklists-preview">
                    <button 
                      className="checklists-toggle"
                      onClick={() => toggleChecklistExpansion(course._id)}
                    >
                      Checklists {expandedChecklists[course._id] ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    
                    {expandedChecklists[course._id] && (
                      <div className="checklists-list">
                        {course.checklists.map((checklist, index) => (
                          <div key={checklist.checklistId} className="checklist-item">
                            <span className="checklist-number">{index + 1}</span>
                            <span className="checklist-name">{checklist.name}</span>
                            <span className="checklist-items-count">
                              {checklist.observationItems?.length || 0} items
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {course.assessments && course.assessments.length > 0 && (
                  <div className="course-assessments-preview">
                    <button 
                      className="assessments-toggle"
                      onClick={() => toggleAssessmentExpansion(course._id)}
                    >
                      Assessments {expandedAssessments[course._id] ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    
                    {expandedAssessments[course._id] && (
                      <div className="assessments-list">
                        {course.assessments.map((assessment, index) => (
                          <div key={assessment.assessmentId} className="assessment-item">
                            <span className="assessment-number">{index + 1}</span>
                            <span className="assessment-name">{assessment.name}</span>
                            <span className="assessment-type-badge">
                              {assessment.type === 'overall' ? 'Grade-based' : 'Yearly'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal-content import-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaFileImport style={{ marginRight: 8 }} />Import Course</h3>
              <button className="modal-close" onClick={closeImportModal}><FaTimes /></button>
            </div>

            <div className="import-modal-body">
              {!importPreview ? (
                <div className="import-upload-section">
                  <p className="import-instructions">
                    Select a course export file (.json) exported from any PMCH E-Logbook system.
                    All modules, checklists, and assessments will be replicated exactly.
                  </p>
                  <label className="import-file-label">
                    <FaUpload />
                    <span>Choose Export File (.json)</span>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportFile}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {importError && <div className="alert alert-error" style={{ margin: '12px 0 0' }}>{importError}</div>}
                </div>
              ) : (
                <div className="import-preview-section">
                  <div className="import-preview-card">
                    <h4>Course Preview</h4>
                    <div className="import-preview-grid">
                      <div className="import-preview-row">
                        <span className="import-preview-label">Name</span>
                        <span className="import-preview-value">{importPreview.course.name}</span>
                      </div>
                      <div className="import-preview-row">
                        <span className="import-preview-label">Level</span>
                        <span className="import-preview-value">{importPreview.course.level}</span>
                      </div>
                      <div className="import-preview-row">
                        <span className="import-preview-label">Duration</span>
                        <span className="import-preview-value">{importPreview.course.duration} months</span>
                      </div>
                      <div className="import-preview-row">
                        <span className="import-preview-label">Modules</span>
                        <span className="import-preview-value">{importPreview.course.modules?.length || 0}</span>
                      </div>
                      <div className="import-preview-row">
                        <span className="import-preview-label">Checklists</span>
                        <span className="import-preview-value">{importPreview.course.checklists?.length || 0}</span>
                      </div>
                      <div className="import-preview-row">
                        <span className="import-preview-label">Assessments</span>
                        <span className="import-preview-value">{importPreview.course.assessments?.length || 0}</span>
                      </div>
                      <div className="import-preview-row">
                        <span className="import-preview-label">Exported At</span>
                        <span className="import-preview-value">
                          {new Date(importPreview.exportedAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="import-code-section">
                    <label className="import-code-label">
                      Course Code
                      {importCodeConflict && (
                        <span className="import-conflict-badge">Conflict — change required</span>
                      )}
                    </label>
                    <input
                      type="text"
                      className={`import-code-input${importCodeConflict ? ' input-conflict' : ''}`}
                      value={importCodeOverride}
                      onChange={(e) => {
                        setImportCodeOverride(e.target.value.toUpperCase());
                        setImportCodeConflict(false);
                        setImportError('');
                      }}
                      placeholder="Course code"
                    />
                    <p className="import-code-hint">
                      If this code already exists on this system, change it to a unique value.
                    </p>
                  </div>

                  {importError && <div className="alert alert-error" style={{ margin: '0 0 12px' }}>{importError}</div>}

                  <div className="import-actions">
                    <button
                      className="btn-primary"
                      onClick={handleImportSubmit}
                      disabled={importLoading || !importCodeOverride.trim()}
                    >
                      {importLoading ? 'Importing...' : <><FaFileImport /> Import Course</>}
                    </button>
                    <button className="btn-secondary" onClick={() => {
                      setImportPreview(null);
                      setImportCodeOverride('');
                      setImportError('');
                      setImportCodeConflict(false);
                    }}>
                      Choose Different File
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Create New Course' : 
                 modalMode === 'edit' ? 'Edit Course' : 'Course Details'}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="course-form">
              <div className="form-row">
                <div className="form-group1">
                  <label>Course Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., M.D. Psychiatry"
                    required
                    disabled={modalMode === 'view'}
                  />
                </div>
                
                <div className="form-group1">
                  <label>Course Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="e.g., MDPSY"
                    required
                    disabled={modalMode === 'view'}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group1">
                  <label>Duration (months) *</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    placeholder="e.g., 36"
                    required
                    disabled={modalMode === 'view'}
                  />
                </div>

                <div className="form-group1">
                  <label>Course Level *</label>
                  <div className="course-level-selector">
                    {['PG', 'UG', 'Super Speciality'].map(lvl => (
                      <label
                        key={lvl}
                        className={`level-option ${formData.level === lvl ? 'level-option-active' : ''} level-option-${lvl.replace(' ', '-')}`}
                      >
                        <input
                          type="radio"
                          name="courseLevel"
                          value={lvl}
                          checked={formData.level === lvl}
                          onChange={(e) => setFormData({...formData, level: e.target.value})}
                          disabled={modalMode === 'view'}
                        />
                        {lvl}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="form-group1">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Course description..."
                  rows="3"
                  disabled={modalMode === 'view'}
                />
              </div>
              
              {/* Modules Section */}
              <div className="modules-section">
                <div className="modules-header">
                  <h4>Course Modules</h4>
                  {modalMode !== 'view' && (
                    <button
                      type="button"
                      className="btn-add-module"
                      onClick={() => {
                        setEditingModuleIndex(null);
                        resetModuleForm();
                        setShowModuleForm(true);
                      }}
                    >
                      <FaPlus /> Add Module
                    </button>
                  )}
                </div>
                
                {showModuleForm && modalMode !== 'view' && (
                  <div className="module-form">
                    <h5>{editingModuleIndex !== null ? `Edit Module: ${formData.modules[editingModuleIndex]?.name}` : 'New Module'}</h5>
                    <div className="form-row">
                      <div className="form-group1">
                        <label>Module Name *</label>
                        <input
                          type="text"
                          value={moduleData.name}
                          onChange={(e) => setModuleData({...moduleData, name: e.target.value})}
                          placeholder="e.g., Undergraduate Teaching"
                        />
                      </div>
                      
                      <div className="form-group1">
                        <label>Module Type *</label>
                        <select
                          value={moduleData.type}
                          onChange={(e) => {
                            setModuleData({
                              ...moduleData,
                              type: e.target.value,
                              fields: [],
                              fieldGroups: [],
                              rowElements: [],
                              mainHeading: ''
                            });
                          }}
                        >
                          <option value="entries">Column-wise (Entries)</option>
                          <option value="counters">Row-wise (Counters)</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="form-group1 allow-attachments-toggle">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={moduleData.allowAttachments || false}
                          onChange={(e) => setModuleData({...moduleData, allowAttachments: e.target.checked})}
                        />
                        <span className="toggle-text">
                          Allow students to attach photos / PDFs with entries in this module
                        </span>
                      </label>
                    </div>

                    {moduleData.type === 'counters' && (
                      <div className="row-wise-config">
                        <div className="form-group1">
                          <label>Main Heading *</label>
                          <input
                            type="text"
                            value={moduleData.mainHeading}
                            onChange={(e) => setModuleData({...moduleData, mainHeading: e.target.value})}
                            placeholder="e.g., Consolidated Operation Record"
                          />
                        </div>
                        
                        <div className="row-elements-section">
                          <h6>Row Elements</h6>
                          <div className="row-element-form">
                            <div className="form-row">
                              <input
                                type="text"
                                placeholder="Row Label"
                                value={rowElementData.label}
                                onChange={(e) => setRowElementData({...rowElementData, label: e.target.value})}
                              />
                              <select
                                value={rowElementData.inputType}
                                onChange={(e) => setRowElementData({...rowElementData, inputType: e.target.value})}
                              >
                                <option value="number">Number Input</option>
                                <option value="select">Dropdown Select</option>
                              </select>
                              {rowElementData.inputType === 'select' && (
                                <input
                                  type="text"
                                  placeholder="Options (comma-separated)"
                                  value={rowElementData.options}
                                  onChange={(e) => setRowElementData({...rowElementData, options: e.target.value.split(',')})}
                                />
                              )}
                              <button 
                                type="button" 
                                className="btn-add-field"
                                onClick={addRowElement}
                              >
                                Add Row
                              </button>
                            </div>
                          </div>
                          
                          {moduleData.rowElements.length > 0 && (
                            <div className="row-elements-list">
                              {moduleData.rowElements.map((element, index) => (
                                <div key={element.id} className="row-element-item">
                                  <span className="element-number">{index + 1}.</span>
                                  <span className="element-label">{element.label}</span>
                                  <span className="element-type">
                                    {element.inputType === 'select' ? 'Dropdown' : 'Number'}
                                  </span>
                                  {element.inputType === 'select' && (
                                    <span className="element-options">
                                      [{element.options.join(', ')}]
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    className="btn-remove-field"
                                    onClick={() => removeRowElement(index)}
                                  >
                                    <FaTimes />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {moduleData.type === 'entries' && (
                      <div className="column-wise-config">
                        <div className="field-groups-section">
                          <div className="section-header">
                            <h6>Field Groups (Optional)</h6>
                            {!isCreatingGroup && (
                              <button 
                                type="button" 
                                className="btn-create-group"
                                onClick={() => setIsCreatingGroup(true)}
                              >
                                <FaFolder /> Create Group
                              </button>
                            )}
                          </div>
                          
                          {isCreatingGroup && (
                            <div className="group-creation-form">
                              <div className="form-group1">
                                <label>Group Name</label>
                                <input
                                  type="text"
                                  value={fieldGroupData.groupName}
                                  onChange={(e) => setFieldGroupData({...fieldGroupData, groupName: e.target.value})}
                                  placeholder="e.g., Duration"
                                />
                              </div>
                              
                              <div className="group-fields-section">
                                <label>Fields in Group</label>
                                <div className="field-form">
                                  <div className="form-row">
                                    <input
                                      type="text"
                                      placeholder="Field Label"
                                      value={fieldData.label}
                                      onChange={(e) => setFieldData({...fieldData, label: e.target.value})}
                                    />
                                    <select
                                      value={fieldData.type}
                                      onChange={(e) => setFieldData({...fieldData, type: e.target.value})}
                                    >
                                      <option value="text">Text</option>
                                      <option value="number">Number</option>
                                      <option value="date">Date</option>
                                      <option value="select">Dropdown</option>
                                      <option value="textarea">Textarea</option>
                                    </select>
                                    <label className="checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={fieldData.required}
                                        onChange={(e) => setFieldData({...fieldData, required: e.target.checked})}
                                      />
                                      Required
                                    </label>
                                    <button 
                                      type="button" 
                                      className="btn-add-field"
                                      onClick={addFieldToCurrentGroup}
                                    >
                                      Add to Group
                                    </button>
                                  </div>
                                  
                                  {fieldData.type === 'select' && (
                                    <input
                                      type="text"
                                      placeholder="Options (comma-separated)"
                                      value={fieldData.options}
                                      onChange={(e) => setFieldData({...fieldData, options: e.target.value})}
                                      className="options-input"
                                    />
                                  )}
                                </div>
                                
                                {currentGroupFields.length > 0 && (
                                  <div className="group-fields-list">
                                    {currentGroupFields.map((field, index) => (
                                      <div key={index} className="field-item">
                                        <span>{field.label}</span>
                                        <span className="field-type">{field.type}</span>
                                        {field.required && <span className="required-badge">Required</span>}
                                        <button
                                          type="button"
                                          className="btn-remove-field"
                                          onClick={() => removeFieldFromCurrentGroup(index)}
                                        >
                                          <FaTimes />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              <div className="group-form-actions">
                                <button 
                                  type="button" 
                                  className="btn-save-group"
                                  onClick={saveFieldGroup}
                                >
                                  Save Group
                                </button>
                                <button 
                                  type="button" 
                                  className="btn-cancel"
                                  onClick={() => {
                                    setIsCreatingGroup(false);
                                    setFieldGroupData({ groupName: '', fields: [] });
                                    setCurrentGroupFields([]);
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {moduleData.fieldGroups.length > 0 && (
                            <div className="field-groups-list">
                              {moduleData.fieldGroups.map((group, index) => (
                                <div key={group.id} className="field-group-card">
                                  <div className="group-header">
                                    <FaFolder className="group-icon" />
                                    <span className="group-name">{group.groupName}</span>
                                    <span className="field-count">{group.fields.length} fields</span>
                                    <button
                                      type="button"
                                      className="btn-remove-group"
                                      onClick={() => removeFieldGroup(index)}
                                    >
                                      <FaTrash />
                                    </button>
                                  </div>
                                  <div className="group-fields">
                                    {group.fields.map((field, fIndex) => (
                                      <span key={fIndex} className="group-field-tag">
                                        {field.label} ({field.type})
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="individual-fields-section">
                          <h6>Individual Fields (Ungrouped)</h6>
                          <div className="field-form">
                            <div className="form-row">
                              <input
                                type="text"
                                placeholder="Field Label"
                                value={fieldData.label}
                                onChange={(e) => setFieldData({...fieldData, label: e.target.value})}
                              />
                              <select
                                value={fieldData.type}
                                onChange={(e) => setFieldData({...fieldData, type: e.target.value})}
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="date">Date</option>
                                <option value="select">Dropdown</option>
                                <option value="textarea">Textarea</option>
                              </select>
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={fieldData.required}
                                  onChange={(e) => setFieldData({...fieldData, required: e.target.checked})}
                                />
                                Required
                              </label>
                              <button 
                                type="button" 
                                className="btn-add-field"
                                onClick={addFieldToModule}
                              >
                                Add Field
                              </button>
                            </div>
                            
                            {fieldData.type === 'select' && (
                              <input
                                type="text"
                                placeholder="Options (comma-separated)"
                                value={fieldData.options}
                                onChange={(e) => setFieldData({...fieldData, options: e.target.value})}
                                className="options-input"
                              />
                            )}
                          </div>
                          
                          {moduleData.fields.length > 0 && (
                            <div className="fields-list">
                              {moduleData.fields.map((field, index) => (
                                <div key={index} className="field-item">
                                  <span>{field.label}</span>
                                  <span className="field-type">{field.type}</span>
                                  {field.required && <span className="required-badge">Required</span>}
                                  <button
                                    type="button"
                                    className="btn-remove-field"
                                    onClick={() => removeFieldFromModule(index)}
                                  >
                                    <FaTimes />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="module-form-actions">
                      <button
                        type="button"
                        className="btn-save-module"
                        onClick={saveModule}
                      >
                        {editingModuleIndex !== null ? 'Update Module' : 'Save Module'}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={() => {
                          setShowModuleForm(false);
                          setEditingModuleIndex(null);
                          resetModuleForm();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                {formData.modules.length > 0 && (
                  <div className="modules-list-form">
                    {formData.modules.map((module, index) => (
                      <div
                        key={module.moduleId}
                        className={`module-card ${module.isActive === false ? 'module-card-hidden' : ''} ${editingModuleIndex === index ? 'module-card-editing' : ''}`}
                      >
                        <div className="module-card-header">
                          <h5>{module.name}</h5>
                          <span className="module-type-badge">{module.type}</span>
                          {module.isActive === false && (
                            <span className="module-hidden-badge">Hidden</span>
                          )}
                          {modalMode !== 'view' && (
                            <div className="module-card-actions">
                              <button
                                type="button"
                                className="btn-icon-sm btn-edit-module"
                                onClick={() => startEditModule(index)}
                                title="Edit Module"
                              >
                                <FaEdit />
                              </button>
                              <button
                                type="button"
                                className="btn-icon-sm btn-duplicate-module"
                                onClick={() => duplicateModule(index)}
                                title="Duplicate Module"
                              >
                                <FaCopy />
                              </button>
                              <button
                                type="button"
                                className={`btn-icon-sm btn-toggle-module ${module.isActive === false ? 'btn-show-module' : ''}`}
                                onClick={() => toggleModuleVisibility(index)}
                                title={module.isActive === false ? 'Show Module to Students' : 'Hide Module from Students'}
                              >
                                {module.isActive === false ? <FaEye /> : <FaEyeSlash />}
                              </button>
                              <button
                                type="button"
                                className="btn-remove-module"
                                onClick={() => removeModule(index)}
                                title="Delete Module"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="module-details">
                          {module.type === 'counters' ? (
                            <>
                              <p>Main Heading: {module.mainHeading}</p>
                              <p>Row Elements: {module.rowElements?.length || module.fields?.length || 0}</p>
                            </>
                          ) : (
                            <>
                              <p>Field Groups: {module.fieldGroups?.length || 0}</p>
                              <p>Individual Fields: {module.fields?.filter(f => !f.groupName).length ?? module.fields?.length ?? 0}</p>
                            </>
                          )}
                          {module.allowAttachments && (
                            <span className="attachment-enabled-badge">📎 Attachments enabled</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Checklists Section */}
              <div className="checklists-section">
                <div className="checklists-header">
                  <h4>Assessment Checklists</h4>
                  {modalMode !== 'view' && (
                    <div className="section-header-actions">
                      <button
                        type="button"
                        className="btn-copy-from-course"
                        onClick={() => {
                          setShowCopyChecklistPanel(!showCopyChecklistPanel);
                          setShowCopyAssessmentPanel(false);
                          setCopySelectedItems([]);
                          setCopySourceCourseId('');
                        }}
                      >
                        <FaCopy /> Copy from Course
                      </button>
                      <button
                        type="button"
                        className="btn-add-checklist"
                        onClick={() => setShowChecklistForm(!showChecklistForm)}
                      >
                        <FaPlus /> Add Checklist
                      </button>
                    </div>
                  )}
                </div>

                {showCopyChecklistPanel && modalMode !== 'view' && (
                  <div className="copy-from-course-panel">
                    <h5><FaCopy /> Copy Checklists from Another Course</h5>
                    <div className="copy-course-select">
                      <label>Select Source Course</label>
                      <select
                        value={copySourceCourseId}
                        onChange={(e) => {
                          setCopySourceCourseId(e.target.value);
                          setCopySelectedItems([]);
                        }}
                      >
                        <option value="">-- Select a course --</option>
                        {courses
                          .filter(c => c._id !== selectedCourse?._id)
                          .map(c => (
                            <option key={c._id} value={c._id}>
                              {c.name} ({c.code})
                            </option>
                          ))
                        }
                      </select>
                    </div>
                    {copySourceCourseId && (() => {
                      const src = courses.find(c => c._id === copySourceCourseId);
                      const srcChecklists = src?.checklists || [];
                      return srcChecklists.length === 0 ? (
                        <p className="copy-no-items">This course has no checklists.</p>
                      ) : (
                        <>
                          <div className="copy-items-list">
                            {srcChecklists.map(cl => (
                              <label key={cl.checklistId} className="copy-item-checkbox">
                                <input
                                  type="checkbox"
                                  checked={copySelectedItems.includes(cl.checklistId)}
                                  onChange={() => toggleCopyItem(cl.checklistId)}
                                />
                                <span>{cl.name}</span>
                                <span className="copy-item-meta">{cl.observationItems?.length || 0} items</span>
                              </label>
                            ))}
                          </div>
                          <div className="copy-panel-actions">
                            <button
                              type="button"
                              className="btn-copy-selected"
                              onClick={copyChecklistsFromCourse}
                              disabled={copySelectedItems.length === 0}
                            >
                              Copy Selected ({copySelectedItems.length})
                            </button>
                            <button
                              type="button"
                              className="btn-cancel"
                              onClick={() => {
                                setShowCopyChecklistPanel(false);
                                setCopySelectedItems([]);
                                setCopySourceCourseId('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
                
                {showChecklistForm && modalMode !== 'view' && (
                  <div className="checklist-form">
                    <h5>New Checklist</h5>
                    <div className="form-group1">
                      <label>Checklist Name *</label>
                      <input
                        type="text"
                        value={checklistData.name}
                        onChange={(e) => setChecklistData({...checklistData, name: e.target.value})}
                        placeholder="e.g., Clinical Skills Assessment"
                      />
                    </div>
                    
                    <div className="observation-items-section">
                      <label>Items for Observation *</label>
                      <div className="observation-input-group">
                        <input
                          type="text"
                          value={observationItemInput}
                          onChange={(e) => setObservationItemInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addObservationItem();
                            }
                          }}
                          placeholder="e.g., Clarity of Presentation"
                        />
                        <button 
                          type="button" 
                          className="btn-add-observation"
                          onClick={addObservationItem}
                        >
                          Add Item
                        </button>
                      </div>
                      
                      {checklistData.observationItems.length > 0 && (
                        <div className="observation-items-list">
                          {checklistData.observationItems.map((item, index) => (
                            <div key={item.id} className="observation-item">
                              <span className="item-number">{index + 1}.</span>
                              <span className="item-name">{item.name}</span>
                              <span className="item-scale">(0-4 scale)</span>
                              <button
                                type="button"
                                className="btn-remove-observation"
                                onClick={() => removeObservationItem(index)}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="checklist-form-actions">
                      <button 
                        type="button" 
                        className="btn-save-checklist"
                        onClick={addChecklist}
                      >
                        Save Checklist
                      </button>
                      <button 
                        type="button" 
                        className="btn-cancel"
                        onClick={() => {
                          setShowChecklistForm(false);
                          setChecklistData({
                            name: '',
                            observationItems: [],
                            maxScore: 4
                          });
                          setObservationItemInput('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                {formData.checklists.length > 0 && (
                  <div className="checklists-list-form">
                    {formData.checklists.map((checklist, index) => (
                      <div key={checklist.checklistId} className="checklist-card">
                        <div className="checklist-card-header">
                          <h5>{checklist.name}</h5>
                          <span className="checklist-badge">
                            {checklist.observationItems.length} items
                          </span>
                          {modalMode !== 'view' && (
                            <button
                              type="button"
                              className="btn-remove-checklist"
                              onClick={() => removeChecklist(index)}
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                        <div className="checklist-details">
                          <div className="checklist-items-preview">
                            {checklist.observationItems.slice(0, 3).map((item, idx) => (
                              <p key={item.id} className="preview-item">
                                • {item.name}
                              </p>
                            ))}
                            {checklist.observationItems.length > 3 && (
                              <p className="more-items">
                                ...and {checklist.observationItems.length - 3} more items
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Assessments Section */}
              <div className="checklists-section">
                <div className="checklists-header">
                  <h4>Assessments</h4>
                  {modalMode !== 'view' && (
                    <div className="section-header-actions">
                      <button
                        type="button"
                        className="btn-copy-from-course"
                        onClick={() => {
                          setShowCopyAssessmentPanel(!showCopyAssessmentPanel);
                          setShowCopyChecklistPanel(false);
                          setCopySelectedItems([]);
                          setCopySourceCourseId('');
                        }}
                      >
                        <FaCopy /> Copy from Course
                      </button>
                      <button
                        type="button"
                        className="btn-add-checklist"
                        onClick={() => setShowAssessmentForm(!showAssessmentForm)}
                      >
                        <FaPlus /> Add Assessment
                      </button>
                    </div>
                  )}
                </div>

                {showCopyAssessmentPanel && modalMode !== 'view' && (
                  <div className="copy-from-course-panel">
                    <h5><FaCopy /> Copy Assessments from Another Course</h5>
                    <div className="copy-course-select">
                      <label>Select Source Course</label>
                      <select
                        value={copySourceCourseId}
                        onChange={(e) => {
                          setCopySourceCourseId(e.target.value);
                          setCopySelectedItems([]);
                        }}
                      >
                        <option value="">-- Select a course --</option>
                        {courses
                          .filter(c => c._id !== selectedCourse?._id)
                          .map(c => (
                            <option key={c._id} value={c._id}>
                              {c.name} ({c.code})
                            </option>
                          ))
                        }
                      </select>
                    </div>
                    {copySourceCourseId && (() => {
                      const src = courses.find(c => c._id === copySourceCourseId);
                      const groups = getAssessmentGroups(src?.assessments || []);
                      return groups.length === 0 ? (
                        <p className="copy-no-items">This course has no assessments.</p>
                      ) : (
                        <>
                          <div className="copy-items-list">
                            {groups.map(group => (
                              <label key={group.name} className="copy-item-checkbox">
                                <input
                                  type="checkbox"
                                  checked={copySelectedItems.includes(group.name)}
                                  onChange={() => toggleCopyItem(group.name)}
                                />
                                <span>{group.name}</span>
                                <span className="copy-item-meta">
                                  {group.type === 'yearly_group'
                                    ? `Yearly (${group.assessments.length} sheets)`
                                    : 'Grade-based'}
                                </span>
                              </label>
                            ))}
                          </div>
                          <div className="copy-panel-actions">
                            <button
                              type="button"
                              className="btn-copy-selected"
                              onClick={copyAssessmentsFromCourse}
                              disabled={copySelectedItems.length === 0}
                            >
                              Copy Selected ({copySelectedItems.length})
                            </button>
                            <button
                              type="button"
                              className="btn-cancel"
                              onClick={() => {
                                setShowCopyAssessmentPanel(false);
                                setCopySelectedItems([]);
                                setCopySourceCourseId('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
                
                {showAssessmentForm && modalMode !== 'view' && (
                  <div className="assessment-form">
                    <h5>New Assessment</h5>
                    
                    <div className="form-row">
                      <div className="form-group1">
                        <label>Assessment Name *</label>
                        <input
                          type="text"
                          value={assessmentData.name}
                          onChange={(e) => setAssessmentData({...assessmentData, name: e.target.value})}
                          placeholder="e.g., Overall Performance Assessment"
                        />
                      </div>
                      
                      <div className="form-group1">
                        <label>Assessment Type *</label>
                        <select
                          value={assessmentData.type}
                          onChange={(e) => setAssessmentData({
                            ...assessmentData,
                            type: e.target.value,
                            selectedMonths: []
                          })}
                        >
                          <option value="overall">Grade-based (Overall)</option>
                          <option value="yearly">Yearly (Score-based)</option>
                        </select>
                      </div>
                    </div>
                    
                    {assessmentData.type === 'overall' && (
                      <div className="grade-config">
                        <div className="form-group1">
                          <label>Grade Scale *</label>
                          <select
                            value={assessmentData.gradeScale}
                            onChange={(e) => setAssessmentData({
                              ...assessmentData,
                              gradeScale: e.target.value
                            })}
                          >
                            <option value="A-J">A-J (10 grades)</option>
                            <option value="custom">Custom Grades</option>
                          </select>
                        </div>
                        
                        {assessmentData.gradeScale === 'custom' && (
                          <div className="form-group1">
                            <label>Custom Grades (comma-separated)</label>
                            <input
                              type="text"
                              value={assessmentData.customGrades}
                              onChange={(e) => setAssessmentData({
                                ...assessmentData,
                                customGrades: e.target.value
                              })}
                              placeholder="e.g., Excellent, Good, Average, Poor"
                            />
                            <small>Enter grades separated by commas</small>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {assessmentData.type === 'yearly' && (
                      <div className="period-selection">
                        <label>Select Months for Each Year *</label>
                        
                        <div className="month-presets">
                          <label>Quick Select:</label>
                          {monthPresets.map((preset, index) => (
                            <button
                              key={index}
                              type="button"
                              className="btn-preset"
                              onClick={() => applyMonthPreset(preset)}
                            >
                              {preset.name}
                            </button>
                          ))}
                        </div>
                        
                        <div className="month-grid">
                          {monthOptions.map((option) => (
                            <label key={option.month} className="month-checkbox">
                              <input
                                type="checkbox"
                                checked={assessmentData.selectedMonths.some(m => m.month === option.month)}
                                onChange={() => toggleMonthSelection(option.month)}
                              />
                              <span>Month {option.month}</span>
                            </label>
                          ))}
                        </div>
                        
                        {assessmentData.selectedMonths.length > 0 && (
                          <div className="selected-periods-preview">
                            <strong>Selected Months:</strong>
                            <div className="selected-tags">
                              {assessmentData.selectedMonths.map((m) => (
                                <span key={m.month} className="period-tag">
                                  Month {m.month}
                                  <FaTimes 
                                    onClick={() => toggleMonthSelection(m.month)}
                                    style={{cursor: 'pointer', marginLeft: '5px'}}
                                  />
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="yearly-info-box">
                          <p><strong>Note:</strong> This will create 4 assessments:</p>
                          <ul>
                            <li>Year 1 - with selected months</li>
                            <li>Year 2 - with selected months</li>
                            <li>Year 3 - with selected months</li>
                            <li>Overall - with Year 1, 2, 3, and Overall columns</li>
                          </ul>
                        </div>
                      </div>
                    )}
                    
                    <div className="assessment-items-section">
                      <label>Assessment Items (Rows) *</label>
                      <div className="assessment-item-input-group">
                        <input
                          type="text"
                          value={assessmentItemInput}
                          onChange={(e) => setAssessmentItemInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addAssessmentItem();
                            }
                          }}
                          placeholder="e.g., Clinical Knowledge, Communication Skills"
                        />
                        <button 
                          type="button" 
                          className="btn-add-assessment-item"
                          onClick={addAssessmentItem}
                        >
                          Add Item
                        </button>
                      </div>
                      
                      {assessmentData.assessmentItems.length > 0 && (
                        <div className="assessment-items-list">
                          {assessmentData.assessmentItems.map((item, index) => (
                            <div key={item.id} className="assessment-item">
                              <span className="item-number">{index + 1}.</span>
                              <span className="item-name">{item.name}</span>
                              <button
                                type="button"
                                className="btn-remove-assessment-item"
                                onClick={() => removeAssessmentItem(index)}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="assessment-form-actions">
                      <button 
                        type="button" 
                        className="btn-save-assessment"
                        onClick={addAssessment}
                      >
                        {assessmentData.type === 'yearly' ? 'Create 4 Assessments' : 'Save Assessment'}
                      </button>
                      <button 
                        type="button" 
                        className="btn-cancel"
                        onClick={() => {
                          setShowAssessmentForm(false);
                          resetAssessmentForm();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                {formData.assessments.length > 0 && (
                  <div className="assessments-list-form">
                    {formData.assessments.map((assessment, index) => (
                      <div key={assessment.assessmentId} className="assessment-card">
                        <div className="assessment-card-header">
                          <h5>{assessment.name}</h5>
                          <div className="assessment-badges">
                            <span className="assessment-type-badge">
                              {assessment.type === 'overall' ? 'Grade-based' : 'Yearly'}
                            </span>
                            {assessment.isYearlyPart && (
                              <span className="assessment-year-badge">
                                {assessment.isOverallSheet ? 'Overall Sheet' : `Year ${assessment.yearNumber}`}
                              </span>
                            )}
                          </div>
                          {modalMode !== 'view' && (
                            <button
                              type="button"
                              className="btn-remove-assessment"
                              onClick={() => removeAssessment(index)}
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                        <div className="assessment-details">
                          {assessment.type === 'overall' ? (
                            <p>Grade Scale: {assessment.gradeScale === 'custom' ? 
                              `Custom (${assessment.customGrades.join(', ')})` : 
                              'A-J'}</p>
                          ) : (
                            <>
                              {assessment.periodType === 'monthly' && (
                                <p>Months: {assessment.selectedMonths.length} selected</p>
                              )}
                              {assessment.periodType === 'yearly' && (
                                <p>Years: {assessment.selectedYears?.length || 0} periods</p>
                              )}
                            </>
                          )}
                          <p>Assessment Items: {assessment.assessmentItems.length}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {modalMode !== 'view' && (
                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    <FaSave /> {modalMode === 'create' ? 'Create Course' : 'Update Course'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCourse;